import type {
  AgeBand,
  CapabilityLevel,
  CaseInput,
  CareLevel,
  CaseType,
  HilloNumber,
  LearnerGrade,
  LocalCase,
  LocalProcedure,
  ProcedureInput,
  ReferralReason,
} from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { initialValues as initialProcedureValues, procedureValuesFrom } from './procedureForm.ts';
import type { ProcedureFormValues } from './procedureForm.ts';

/**
 * The case form's state and the pure rules around nesting.
 *
 * The rule this file exists to get right: a nested procedure holds its **own
 * copy** of the case's date, age band and capability level. It does not point
 * at the case. That is what lets an intubation at 21:00 sit inside an admission
 * timed 20:45, and what keeps the PDF and the dashboard able to count
 * procedures without joining anything.
 *
 * Inheritance therefore has to answer one question: when the case's date
 * changes, which procedures follow? The answer is "those the user has not
 * touched". Each draft records which of the three fields it has overridden, and
 * an overridden field never moves again.
 */

export type CaseFormValues = {
  caseType: CaseType;
  title: string;
  occurredAt: Date;
  ageBand: AgeBand | null;
  capabilityLevel: CapabilityLevel | null;
  reasonsForReferral: readonly ReferralReason[];
  /** ICS level of care, 0–3. Null means not recorded — and 0 is not null. */
  careLevel: CareLevel | null;
  /** Who the trainee taught during this case. Empty means they did not. */
  teachingGrades: readonly LearnerGrade[];
  note: string;
  hillos: readonly HilloNumber[];
};

/** The three fields a procedure copies from its case. */
export const INHERITED_FIELDS = ['occurredAt', 'ageBand', 'capabilityLevel'] as const;
export type InheritedField = (typeof INHERITED_FIELDS)[number];

export type NestedProcedureDraft = {
  /**
   * Identity for the list and for editing before anything is saved. For a
   * procedure already stored this is its real record id, which is how the save
   * path tells an addition from an edit.
   */
  draftId: string;
  saved: boolean;
  values: ProcedureFormValues;
  /** Fields the user has changed on this procedure, which stop following the case. */
  overridden: readonly InheritedField[];
};

export function initialCaseValues(caseType: CaseType, occurredAt: Date): CaseFormValues {
  return {
    caseType,
    title: '',
    occurredAt,
    ageBand: null,
    capabilityLevel: null,
    reasonsForReferral: [],
    careLevel: null,
    teachingGrades: [],
    note: '',
    hillos: [],
  };
}

/**
 * Loads a stored case back into the form, for editing — or an unsaved
 * duplicate of one, which is the same shape minus the stored fields.
 *
 * Both callers go through here rather than building the object themselves. The
 * duplicate path used to hand-roll it, and adding a field to a case silently
 * left duplicates without it.
 */
export function caseValuesFrom(record: LocalCase | CaseInput): CaseFormValues {
  return {
    caseType: record.caseType,
    title: record.title,
    occurredAt: record.occurredAt,
    ageBand: record.ageBand,
    capabilityLevel: record.capabilityLevel,
    reasonsForReferral: record.reasonsForReferral ?? [],
    // `?? null`, never `|| null`: level 0 is a real value and would be lost.
    careLevel: record.careLevel ?? null,
    teachingGrades: record.teachingGrades ?? [],
    note: record.note ?? '',
    hillos: record.hillos ?? [],
  };
}

/**
 * A new procedure inside a case: the catalogue defaults, with the case's date,
 * age band and capability level copied in.
 *
 * The case's HiLLO tags are deliberately not copied. DATA_MODEL.md is explicit
 * that a nested procedure starts from its own suggestions — an arterial line
 * evidences what an arterial line evidences, whatever the admission was for.
 */
export function nestedProcedureDraft(
  caseValues: CaseFormValues,
  procedureKey: string,
): NestedProcedureDraft {
  return {
    draftId: uuidv7(),
    saved: false,
    values: {
      ...initialProcedureValues(procedureKey, caseValues.occurredAt),
      ageBand: caseValues.ageBand,
      capabilityLevel: caseValues.capabilityLevel,
    },
    overridden: [],
  };
}

/** Loads a stored procedure back into the form as an existing child. */
export function nestedDraftFrom(record: LocalProcedure): NestedProcedureDraft {
  return {
    draftId: record.id,
    saved: true,
    values: procedureValuesFrom(record),
    // A stored procedure is its own record. It never silently follows its case
    // again; changing the case offers to apply the change instead.
    overridden: [...INHERITED_FIELDS],
  };
}

/**
 * Records that the user has edited one of the three inherited fields on a
 * procedure, so it stops following the case.
 */
export function markOverridden(
  draft: NestedProcedureDraft,
  previous: ProcedureFormValues,
): NestedProcedureDraft {
  const changed = INHERITED_FIELDS.filter((field) => {
    if (field === 'occurredAt') {
      return draft.values.occurredAt.getTime() !== previous.occurredAt.getTime();
    }
    return draft.values[field] !== previous[field];
  });

  if (changed.length === 0) return draft;

  return {
    ...draft,
    overridden: [...new Set([...draft.overridden, ...changed])],
  };
}

/**
 * Pushes a change to the case down into the unsaved procedures that have not
 * overridden that field.
 *
 * Only unsaved drafts follow along. Nothing already written to the database is
 * touched here — that needs the explicit offer, because it is an edit to a
 * record the trainee believes they have already made.
 */
export function applyCaseValuesToDrafts(
  drafts: readonly NestedProcedureDraft[],
  caseValues: CaseFormValues,
): NestedProcedureDraft[] {
  return drafts.map((draft) => {
    if (draft.saved) return draft;

    const follows = (field: InheritedField): boolean => !draft.overridden.includes(field);

    return {
      ...draft,
      values: {
        ...draft.values,
        ...(follows('occurredAt') ? { occurredAt: caseValues.occurredAt } : {}),
        ...(follows('ageBand') ? { ageBand: caseValues.ageBand } : {}),
        ...(follows('capabilityLevel') ? { capabilityLevel: caseValues.capabilityLevel } : {}),
      },
    };
  });
}

/**
 * Which of the three inherited fields an edit has changed on the case itself.
 * A non-empty result is what triggers the one-time offer to apply the change to
 * the case's stored procedures.
 */
export function inheritedChanges(
  before: CaseFormValues,
  after: CaseFormValues,
): Partial<Pick<ProcedureInput, 'occurredAt' | 'ageBand' | 'capabilityLevel'>> {
  const changes: Partial<Pick<ProcedureInput, 'occurredAt' | 'ageBand' | 'capabilityLevel'>> = {};

  if (before.occurredAt.getTime() !== after.occurredAt.getTime()) {
    changes.occurredAt = after.occurredAt;
  }
  if (before.ageBand !== after.ageBand && after.ageBand !== null) {
    changes.ageBand = after.ageBand;
  }
  if (before.capabilityLevel !== after.capabilityLevel && after.capabilityLevel !== null) {
    changes.capabilityLevel = after.capabilityLevel;
  }

  return changes;
}

/**
 * The fields that actually differ between a stored procedure and the edited
 * version of it.
 *
 * Only these are sent to the repository. Sending the whole record instead would
 * make every field an explicit edit, which would silently out-rank the case's
 * inherited date and age band and quietly undo an "apply to all" — and it would
 * re-queue every procedure in a case for sync whenever its title was corrected.
 */
export function procedureChanges(
  before: ProcedureInput,
  after: ProcedureInput,
): Partial<ProcedureInput> {
  const changes: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);

  for (const key of keys) {
    const from = (before as Record<string, unknown>)[key];
    const to = (after as Record<string, unknown>)[key];

    if (from instanceof Date && to instanceof Date) {
      if (from.getTime() !== to.getTime()) changes[key] = to;
      continue;
    }

    if (Array.isArray(from) && Array.isArray(to)) {
      if (JSON.stringify(from) !== JSON.stringify(to)) changes[key] = to;
      continue;
    }

    if (from !== to) changes[key] = to;
  }

  return changes as Partial<ProcedureInput>;
}

/** Null until every required field has been chosen. Same contract as procedures. */
export function buildCaseInput(values: CaseFormValues): CaseInput | null {
  const title = values.title.trim();
  if (values.ageBand === null || values.capabilityLevel === null || title === '') return null;

  const note = values.note.trim();

  return {
    caseType: values.caseType,
    title,
    occurredAt: values.occurredAt,
    ageBand: values.ageBand,
    capabilityLevel: values.capabilityLevel,
    ...(values.reasonsForReferral.length === 0
      ? {}
      : { reasonsForReferral: [...values.reasonsForReferral] }),
    // `=== null`, not truthiness: a level 0 case must survive the save.
    ...(values.careLevel === null ? {} : { careLevel: values.careLevel }),
    ...(values.teachingGrades.length === 0 ? {} : { teachingGrades: [...values.teachingGrades] }),
    ...(values.hillos.length === 0 ? {} : { hillos: [...values.hillos] }),
    ...(note === '' ? {} : { note }),
  };
}

export type MissingCaseField = 'title' | 'ageBand' | 'capabilityLevel';

export function missingCaseFields(values: CaseFormValues): MissingCaseField[] {
  const missing: MissingCaseField[] = [];
  if (values.title.trim() === '') missing.push('title');
  if (values.capabilityLevel === null) missing.push('capabilityLevel');
  if (values.ageBand === null) missing.push('ageBand');
  return missing;
}
